import NpawPlugin from 'npaw-plugin';
import { ClientPlatform, Media, MediaCapabilities, PlayerOptionsNpaw } from '../types';
import { getFirstCapabilityValue } from './hasCapability';
import { ChromecastCafAdapter } from '../npawAdapters/chromecastCaf/ChromecastCaf';
import { TheoplayerAdapter } from '../npawAdapters/theoplayer/TheoplayerAdapter';
import 'core-js/actual/array';

interface AnalyticsOptions {
  'content.title'?: string;
  'content.program'?: string;
  'content.duration'?: number;
  'content.isLive'?: boolean;
  'content.fps'?: number;
  'content.bitrate'?: number;
  'content.totalBytes'?: number;
  'content.throughput'?: number;
  'content.sendTotalBytes'?: boolean;
  'content.rendition'?: string;
  'content.resource'?: string;
  'content.cdn'?: string;
  'content.cdnNode'?: string;
  'content.cdnType'?: number;
  'content.streamingProtocol'?: string;
  'content.saga'?: string;
  'content.tvShow'?: string;
  'content.season'?: string;
  'content.episodeTitle'?: string;
  'content.channel'?: string;
  'content.id'?: string;
  'content.imdbId'?: string;
  'content.gracenoteId'?: string;
  'content.type'?: string;
  'content.genre'?: string;
  'content.language'?: string;
  'content.subtitles'?: string;
  'content.contractedResolution'?: string;
  'content.cost'?: string;
  'content.price'?: string;
  'content.playbackType'?: string;
  'content.drm'?: string;
  'content.encoding.videoCodec'?: string;
  'content.encoding.audioCodec'?: string;
  'content.encoding.codecSettings'?: string;
  'content.encoding.codecProfile'?: string;
  'content.encoding.containerFormat'?: string;
  'content.transactionCode'?: string;

  'user.name'?: string;
  'user.email'?: string;
  'user.type'?: string;
  'user.obfuscateIp'?: boolean;
  'user.privacyProtocol'?: 'optin' | 'optout';
  'network.ip'?: string;
  'network.isp'?: string;
  'network.connectionType'?: string;
  'device.code'?: string;
  'device.id'?: string;
  'device.name'?: string;
  'device.model'?: string;
  'device.brand'?: string;
  'device.type'?: string;
  'device.osName'?: string;
  'device.osVersion'?: string;
  'device.browserName'?: string;
  'device.browserVersion'?: string;
  'device.browserType'?: string;
  'device.browserEngine'?: string;
  'device.EDID'?: string | Uint8Array;
  'device.isAnonymous'?: boolean;
  'ad.blockerDetected'?: boolean;
  enabled?: boolean;
  'app.name'?: string;
  'app.releaseVersion'?: string;
  'parse.cdnNode'?: boolean;
  'parse.cdnNode.list'?: string[];
  'parse.cdnNameHeader'?: string;
  'parse.cdnNodeHeader'?: string;
  'parse.manifest'?: boolean;
  'parse.manifest.auth'?: {
    authorization: string;
  };
  offline?: boolean;
  disableCookies?: boolean;
  forceCookies?: boolean;
  disableStorage?: boolean;
  authType?: 'Bearer';
  authToken?: string;
  forceInit?: boolean;

  [key: string]: any; // Allow other custom dimensions
}

type NpawOptions = {
  npaw: PlayerOptionsNpaw;
  clientPlatform: ClientPlatform;
};

type VideoOptions = {
  metadata: Media['metadata'];
  tracking: {
    npaw: Media['tracking']['npaw'];
  };
  sourceCapabilities?: Media['sourceCapabilities'];
  mediaCapabilities?: MediaCapabilities;
  type?: Media['type'];
};

export class NpawPluginManager {
  private npaw: NpawPlugin;
  private analyticsOptions: AnalyticsOptions = {};

  constructor(player: any | undefined, options: NpawOptions) {
    this.npaw = new NpawPlugin(options.npaw.accountCode, { components: { balancer: false } });

    if (!player) {
      this.npaw.registerDefaultAdapter();
    } else {
      const adapter = this.getNpawAdapter(options.clientPlatform.name);
      this.npaw.registerAdapterFromClass(player, adapter);
    }

    if (!options.npaw.isDev) {
      this.disableDiagnostics();
    }

    this.setAnalyticsOptions(options);
  }

  public destroy() {
    this.npaw.destroy();
  }

  public enableDebug() {
    this.npaw.setLogLevel(2);
  }

  public setAnalyticsOptions(options: NpawOptions) {
    const analyticsOptions = {
      'device.name': options.clientPlatform.device?.name,
      'device.model': options.clientPlatform.device?.model,
      'device.osName': options.clientPlatform.device?.osName,
      'device.osVersion': options.clientPlatform.device?.osVersion,
      'app.name': options.clientPlatform.name,
      'app.releaseVersion': options.clientPlatform.version,
      'content.isLive.noSeek': true,
    } satisfies AnalyticsOptions;

    this.analyticsOptions = { ...this.analyticsOptions, ...options };
    this.npaw.setAnalyticsOptions(analyticsOptions);
  }

  public setVideoOptions({
    metadata,
    tracking,
    sourceCapabilities,
    type,
    mediaCapabilities,
  }: VideoOptions) {
    const mediaCapabilityOptions =
      mediaCapabilities &&
      ({
        'content.customDimension.4': mediaCapabilities.hdcpVersion,
        'content.customDimension.5': mediaCapabilities.drmSecurityLevel,
      } satisfies AnalyticsOptions);

    const sourceCapabilityOptions =
      sourceCapabilities &&
      ({
        'content.encoding.videoCodec': getFirstCapabilityValue(sourceCapabilities, 'CODEC'),
        'content.encoding.audioCodec': getFirstCapabilityValue(sourceCapabilities, 'AUDIOCODEC'),
        'content.fps':
          getFirstCapabilityValue(sourceCapabilities, 'FRAMERATE') ?? metadata.isLive ? 50 : 25,
        'content.drm': getFirstCapabilityValue(sourceCapabilities, 'DRM'),
        'content.customDimension.3':
          getFirstCapabilityValue(sourceCapabilities, 'DYNAMICRANGE') ?? 'SDR',
      } satisfies AnalyticsOptions);

    const analyticsOptions = {
      'content.isLive': metadata.isLive,
      'content.title': tracking.npaw.presentationTitle ?? metadata.title,
      'content.id': tracking.npaw.guid ?? '',
      'content.genre': tracking.npaw.genre,
      'content.type': tracking.npaw.type,
      'user.name': tracking.npaw.viewerId ?? undefined,
      'content.customDimension.1': tracking.npaw.deviceBlacklistString ?? undefined,
      'content.customDimension.2': tracking.npaw.region ?? undefined,
      'content.streamingProtocol': type === 'application/dash+xml' ? 'DASH' : 'HLS',
      ...sourceCapabilityOptions,
      ...mediaCapabilityOptions,
    } satisfies AnalyticsOptions;

    this.npaw.setVideoOptions(analyticsOptions);
  }

  // Custom events emitted to NPAW
  public fireStop(triggeredEvent?: string) {
    this.npaw.getAdapter().fireStop({}, triggeredEvent);
  }

  public fireBusinessError(message: string) {
    const triggerName = 'businessError';
    this.npaw.getAdapter().fireStart({}, triggerName);
    this.npaw.getAdapter().fireError('Business Error', message, {}, '', triggerName, true);
    this.npaw.getAdapter().fireStop({}, triggerName);
  }

  private getNpawAdapter(adapterType: ClientPlatform['name']) {
    switch (adapterType) {
      case 'SmartTV':
      case 'Web':
        return TheoplayerAdapter;
      case 'Chromecast':
        return ChromecastCafAdapter;
    }
  }

  private disableDiagnostics() {
    this.npaw.diagnostic.setBalancerEnabled(false);
    this.npaw.diagnostic.setAdsAnalyticsEnabled(false);
    this.npaw.diagnostic.setVideoAnalyticsEnabled(false);
  }
}
